import { Typography, Alert, Button, CurrencyInput, Modal, Progress, Table } from "best-common-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { bulkPurchaseOpportunityTransaction } from "../../api/RequesTixApi";
import {
  AdminPurchaseOpportunityOverviewEntryDTO,
  PurchaseOppBulkTransactionDTO,
  PurchaseOppBulkTx,
} from "../../types/PurchaseOpportunity";

type BulkFailure = {
  purchaseOpportunityEntryId: number;
  error: string;
};

type BulkProgress = {
  processed: number;
  successful: number;
  failed: number;
};

type BulkRunSnapshot = {
  amount: number;
  isCharge: boolean;
  total: number;
  transactions: PurchaseOppBulkTx[];
};

type AdminPurchaseOpportunityBulkTransactionsProps = {
  isOpen: boolean;
  close: () => void;
  isCharge: boolean;
  amount?: number;
  purchaseOpportunities: AdminPurchaseOpportunityOverviewEntryDTO[];
  reload: () => Promise<void>;
  onProcessingChange: (processing: boolean) => void;
};

const initialProgress: BulkProgress = { processed: 0, successful: 0, failed: 0 };

const AdminPurchaseOpportunityBulkTransactions = ({
  isOpen,
  close,
  isCharge,
  amount: amountProps = 0,
  purchaseOpportunities,
  reload,
  onProcessingChange,
}: AdminPurchaseOpportunityBulkTransactionsProps) => {
  const [amount, setAmount] = useState<number>(amountProps);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<BulkProgress>(initialProgress);
  const [failures, setFailures] = useState<BulkFailure[]>([]);
  const [uncertainEntryId, setUncertainEntryId] = useState<number>();
  const [notSubmitted, setNotSubmitted] = useState<number>(0);
  const [refreshFailed, setRefreshFailed] = useState<boolean>(false);
  const [hasRun, setHasRun] = useState<boolean>(false);
  const [runSnapshot, setRunSnapshot] = useState<BulkRunSnapshot>();
  const mountedRef = useRef<boolean>(true);
  const continueRef = useRef<boolean>(true);
  const runningRef = useRef<boolean>(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      continueRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!processing) {
      return;
    }

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [processing]);

  useEffect(() => {
    if (!isOpen && !processing) {
      setAmount(amountProps);
      setProgress(initialProgress);
      setFailures([]);
      setUncertainEntryId(undefined);
      setNotSubmitted(0);
      setRefreshFailed(false);
      setHasRun(false);
      setRunSnapshot(undefined);
    }
  }, [amountProps, isOpen, processing]);

  const hasMissingBilling = purchaseOpportunities.some((opportunity) => opportunity.userBillingId === null);
  const requiredStatusId = isCharge ? 2 : 4;
  const hasIneligibleStatus = purchaseOpportunities.some(
    (opportunity) => opportunity.purchaseOpportunityStatus.purchaseOpportunityStatusId !== requiredStatusId,
  );
  const canStart = useMemo(
    () =>
      Number.isFinite(amount) &&
      amount > 0 &&
      purchaseOpportunities.length > 0 &&
      !hasMissingBilling &&
      !hasIneligibleStatus &&
      !processing &&
      !hasRun,
    [amount, hasIneligibleStatus, hasMissingBilling, hasRun, processing, purchaseOpportunities.length],
  );

  const displayTotal = runSnapshot?.total ?? purchaseOpportunities.length;
  const operationIsCharge = runSnapshot?.isCharge ?? isCharge;
  const progressValue = displayTotal ? Math.round((progress.processed / displayTotal) * 100) : 0;

  const kickOffTx = async () => {
    if (runningRef.current || !canStart) {
      return;
    }

    const transactions = purchaseOpportunities.reduce<PurchaseOppBulkTx[]>((snapshotTransactions, opportunity) => {
      const userBillingId = opportunity.userBillingId;
      if (userBillingId) {
        snapshotTransactions.push({
          purchaseOpportunityEntryId: opportunity.purchaseOpportunityEntryId,
          userBillingId,
        });
      }
      return snapshotTransactions;
    }, []);
    if (transactions.length !== purchaseOpportunities.length) {
      return;
    }

    const snapshot: BulkRunSnapshot = {
      amount,
      isCharge,
      total: transactions.length,
      transactions,
    };

    runningRef.current = true;
    continueRef.current = true;
    setHasRun(true);
    setRunSnapshot(snapshot);
    setProcessing(true);
    onProcessingChange(true);
    setProgress(initialProgress);
    setFailures([]);
    setUncertainEntryId(undefined);
    setNotSubmitted(0);
    setRefreshFailed(false);

    let processed = 0;
    let successful = 0;
    let failed = 0;
    const runFailures: BulkFailure[] = [];
    let uncertain: number | undefined;

    try {
      for (const transaction of snapshot.transactions) {
        if (!continueRef.current) {
          break;
        }

        const request: PurchaseOppBulkTransactionDTO = {
          purchaseOpportunities: [transaction],
          amount: snapshot.amount,
          isCharge: snapshot.isCharge,
        };

        try {
          const result = await bulkPurchaseOpportunityTransaction(request);
          if (!Array.isArray(result)) {
            throw new Error("Unexpected bulk transaction response");
          }

          if (result.length === 0) {
            processed += 1;
            successful += 1;
          } else if (
            result.length === 1 &&
            result[0].purchaseOpportunityId === transaction.purchaseOpportunityEntryId &&
            typeof result[0].error === "string" &&
            result[0].error.trim().length > 0
          ) {
            if (result[0].uncertain === true) {
              uncertain = transaction.purchaseOpportunityEntryId;
              break;
            }
            processed += 1;
            failed += 1;
            runFailures.push({
              purchaseOpportunityEntryId: transaction.purchaseOpportunityEntryId,
              error: result[0].error,
            });
          } else {
            throw new Error("Unexpected bulk transaction error response");
          }
        } catch {
          uncertain = transaction.purchaseOpportunityEntryId;
          break;
        }

        if (mountedRef.current) {
          setProgress({ processed, successful, failed });
          setFailures([...runFailures]);
        }
      }
    } finally {
      const remaining = Math.max(snapshot.total - processed - (uncertain ? 1 : 0), 0);
      let refreshDidFail = false;
      try {
        await reload();
      } catch {
        refreshDidFail = true;
        if (mountedRef.current) {
          setRefreshFailed(true);
        }
      }

      if (mountedRef.current) {
        setProgress({ processed, successful, failed });
        setFailures(runFailures);
        setUncertainEntryId(uncertain);
        setNotSubmitted(remaining);
        setProcessing(false);
        onProcessingChange(false);

        if (runFailures.length === 0 && !uncertain && !refreshDidFail) {
          close();
        }
      }
      runningRef.current = false;
    }
  };

  return (
    <Modal show={isOpen} ariaLabel={`Bulk ${operationIsCharge ? "charge" : "refund"}`}>
      <Modal.Header close={processing ? undefined : close} escapeClose={!processing}>
        <Typography elementType="span" variant="titleMedium">
          Bulk {operationIsCharge ? "Charge" : "Refund"}
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <CurrencyInput
          id="amount"
          label={`${operationIsCharge ? "Charge" : "Refund"} Amount`}
          value={amount}
          onChange={(value: number) => setAmount(value)}
          required
          gutterBottom
          disabled={processing || hasRun}
        />

        <Typography elementType="div" className="bcr-mb-2" aria-live="polite">
          <b>{progress.processed}</b> of <b>{displayTotal}</b> processed · {progress.successful} successful ·{" "}
          {progress.failed} failed
        </Typography>
        <Progress value={progressValue} animated={processing} displayValue />

        {processing && (
          <Alert type="info" className="bcr-mt-2">
            <Typography elementType="div">
              Keep this page open while transactions are processed one at a time.
            </Typography>
          </Alert>
        )}
        {hasMissingBilling && !hasRun && (
          <Alert type="danger" className="bcr-mt-2">
            <Typography elementType="div">
              One or more selected entries do not have a billing method and cannot be submitted.
            </Typography>
          </Alert>
        )}
        {hasIneligibleStatus && !hasRun && (
          <Alert type="danger" className="bcr-mt-2">
            <Typography elementType="div">
              All selected entries must be ready to be {operationIsCharge ? "charged" : "refunded"}.
            </Typography>
          </Alert>
        )}
        {!!uncertainEntryId && (
          <Alert type="warning" className="bcr-mt-2">
            <Typography elementType="div">
              The result for entry {uncertainEntryId} is uncertain because Requestix did not return the expected
              response. Check its payment status before retrying. {notSubmitted} remaining{" "}
              {notSubmitted === 1 ? "entry was" : "entries were"} not submitted.
            </Typography>
          </Alert>
        )}
        {refreshFailed && (
          <Alert type="warning" className="bcr-mt-2">
            <Typography elementType="div">
              Payment processing finished, but the opportunity could not be refreshed. Reload the page before taking
              another payment action.
            </Typography>
          </Alert>
        )}

        {failures.length > 0 && (
          <Table className="bcr-mt-2">
            <Table.Head>
              <Table.Header>Purchase Entry ID</Table.Header>
              <Table.Header>Error</Table.Header>
            </Table.Head>
            <Table.Body>
              {failures.map((failure) => (
                <Table.Row key={failure.purchaseOpportunityEntryId}>
                  <Table.TdInfo>
                    <Typography elementType="span">{failure.purchaseOpportunityEntryId}</Typography>
                  </Table.TdInfo>
                  <Table.Td>
                    <Typography elementType="span">{failure.error}</Typography>
                  </Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="bcr-me-2"
          variant="primary"
          onClick={() => void kickOffTx()}
          loading={processing}
          disabled={!canStart}
        >
          {operationIsCharge ? "Charge" : "Refund"} {displayTotal} {displayTotal === 1 ? "Entry" : "Entries"}
        </Button>
        <Button variant="default" onClick={close} disabled={processing}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminPurchaseOpportunityBulkTransactions;
